'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2025 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Beam
//***********************************************************************************
//***********************************************************************************

import { cn_element } from './cn_element';
import { cn_add, cn_box, cn_cart, cn_clone, cn_dot, cn_middle, cn_mul, cn_normal, cn_normalize, cn_polar, cn_sub, cnx_add, cnx_mul, cnx_normalize, cnx_sub } from '../utils/cn_utilities';
import { fh_clone, fh_cross, fh_extruded_polygon, fh_matrix, fh_mul, fh_normalize, fh_polygon, fh_sub } from '@acenv/fh-3d-viewer';
import { cn_storey } from './cn_storey';
import { cn_element_visitor } from '../utils/visitors/cn_element_visitor';
import { cn_scene } from './cn_scene';
import { cn_space } from './cn_space';
import { logger } from '../utils/cn_logger';
import { cn_beam_column_type } from './cn_beam_column_type';
import { cn_column } from './cn_column';
import { cn_opening } from './cn_opening';
import { cn_wall } from './cn_wall';
import { cn_object_instance } from './cn_object_instance';

/**
 * @class beam
 */
export class cn_duplicator extends cn_element {
    /**
     *
     * @param {cn_scene} scene
     */
    constructor(scene) {
        super(scene);
        this.class_name = 'cn_duplicator';

        //*** Model data
        this.elements = [];
        this.center = [0, 0];
        this.orientation = 0;
        this.cells = [1, 1];
        this.spacings = [1, 1];
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        var json = this.serialize_element();
        json.elements = this.elements.map(e => (e) ? e.ID : null);
        json.center = this.center;
        json.orientation = this.orientation;
        json.cells = this.cells;
        json.spacings = this.spacings;
        return json;
    }

    static unserialize(json, scene) {
        if (typeof (json.ID) != 'string') return false;
        if (typeof (json.elements) != 'object') return false;

        var dup = new cn_duplicator(scene);
        dup.unserialize_element(json);
        dup.center = json.center;
        dup.orientation = json.orientation;
        dup.cells = json.cells;
        dup.spacings = json.spacings;
        dup.elements = json.elements.map(eid => (eid) ? scene.find_element_by_id(eid) : null);
        dup.elements.forEach(elt => {
            if (elt) elt.duplicator = dup;
        });

        scene.duplicators.push(dup);
    }

    /**
     * Returns true if element is set on a wall.
     * @returns {cn_wall}
     */
    get_wall(element = null) {
        if (this.elements.length == 0) return null;
        if (element) {
            if (element.constructor == cn_opening) return element.wall;
            if (element.constructor == cn_object_instance) return element.object.place_on_wall() ? element._contact_wall : null;
            return null;
        }
        if (this.elements[0].constructor == cn_opening) return this.elements[0].wall;
        if (this.elements[0].constructor == cn_object_instance) return this.elements[0].object.place_on_wall() ? this.elements[0]._contact_wall : null;
        return null;
    }

    /**
     * Returns the side of the wall
     * @returns {number}
     */
    get_wall_side() {
        if (this.elements.length == 0) return 0;
        if (this.elements[0].constructor == cn_object_instance)
            return this.elements[0]._contact_wall_side;
        return 0;
    }

    //***********************************************************************************
    //**** initiate
    //***********************************************************************************
    /**
     *
     * @param {cn_element[]} elements
     * @returns {cn_duplicator}
     */
    static initiate(elements) {
        let elt = elements.find(elt => elt.duplicator);
        if (elt) {
            if (elt.duplicator.get_wall())
                elt.duplicator.initiate_wall(elt);
            return elt.duplicator;
        }

        let scene = elements[0].parent;
        while (scene && scene.constructor != cn_scene) scene = scene.parent;
        if (!scene) return null;

        scene.building.transaction_manager.push_transaction("Duplication d'éléments");
        scene.building.transaction_manager.push_item_set(scene, "duplicators");

        let dup = new cn_duplicator(scene);
        scene.duplicators.push(dup);

        dup.elements = elements;

        if (elements[0].constructor == cn_object_instance)
            dup.elements = dup.elements.filter(elt => elt.object == elements[0].object);

        if (dup.get_wall()) {
            dup.initiate_wall(dup.elements[0]);
        }
        else {
            dup.elements.forEach(elt => elt.duplicator = dup);
            dup.cells = [elements.length, 1];
            dup.spacings = [0, 0];
            dup.elements.forEach(elt => {
                let sz = cn_duplicator.get_element_size(elt);
                if (dup.spacings[0] < sz[0]) dup.spacings[0] = sz[0];
                if (dup.spacings[1] < sz[1]) dup.spacings[1] = sz[1];
            })
            dup.spacings[0] *= 2;
            dup.spacings[1] *= 2;
            if (dup.spacings[0] < 0.001) dup.spacings[0] = 1;
            if (dup.spacings[1] < 0.001) dup.spacings[1] = 1;
            dup.center = dup.get_element_position(dup.elements[0]);
        }
        return dup;
    }

    static get_element_size(element) {
        if (element.constructor == cn_column)
            return [element.element_type.width, element.element_type.thickness];
        if (element.constructor == cn_opening)
            return [element.get_width(), 1];
        if (element.constructor == cn_object_instance)
            return [element.size[0], element.size[1]];
        return [1, 1];
    }

    initiate_wall(element) {
        let wall = this.get_wall(element);
        let field = this._get_field(element);
        this.elements = this.elements.filter(elt => {
            if (element.parent[field].includes(elt)) {
                elt.duplicator = this;
                return true;
            }
            if (elt.duplicator == this) elt.duplicator = null;
            return false;
        });

        let posmin = 10000;
        let posmax = -10000;
        let full_width = 0;
        this.elements.forEach(elt => {
            let wp = this.get_wall_position(elt);
            if (posmin > wp[0]) posmin = wp[0];
            if (posmax < wp[1]) posmax = wp[1];
            full_width += wp[1] - wp[0];
        });
        // @ts-ignore
        this.center = cn_middle(wall.compute_side_position_from_abcissa(0.5 * (posmin + posmax), 0), wall.compute_side_position_from_abcissa(0.5 * (posmin + posmax), 1));
        this.orientation = cn_polar(wall.bounds.direction)[1];
        this.spacings = [1, 1];
        if (this.elements.length > 1) {
            let full_spacing = (posmax - posmin - full_width) / (this.elements.length - 1);
            this.spacings[0] = (posmax - posmin + full_spacing) / this.elements.length;
        }
        else {
            this.spacings[0] = 2 * cn_duplicator.get_element_size(this.elements[0])[0];
        }
        if (this.spacings[0] < 0.001) this.spacings[0] = 1;
        this.cells = [this.elements.length, 1];
    }

    remove(transaction_manager) {
        let index = this.parent.duplicators.indexOf(this);
        if (index < 0) return;
        if (transaction_manager) transaction_manager.push_item_set(this.parent, ["duplicators"]);
        this.parent.duplicators.splice(index, 1);

        this.elements.forEach(elt => {
            if (elt) {
                if (transaction_manager) transaction_manager.push_item_set(elt, ["duplicator"]);
                elt.duplicator = null;
            }
        });
    }

    get_wall_position(element) {
        if (element.constructor == cn_opening) {
            return [element.position, element.position + element.get_width()];
        }
        if (element.constructor == cn_object_instance) {
            return [element._contact_wall_position, element._contact_wall_position];
        }
        return [0, 0];
    }
    /**
     *
     * @param {cn_element} element
     * @returns
     */
    get_element_position(element) {
        let bbox = element.get_bounding_box();
        return cn_add(bbox.posmin, cn_mul(bbox.size, 0.5));
    }

    set_element_position(element_index, center, wall, wall_side, transaction_manager) {
        if (element_index < 0 || element_index >= this.elements.length) return;
        let element = this.elements[element_index];
        if (!element) return;
        if (element.constructor == cn_column) {
            if (transaction_manager) transaction_manager.push_item_set(element, ["position", "orientation"]);
            element.position = cn_clone(center);
            element.orientation = this.orientation * 180 / Math.PI;
        }
        else if (element.constructor == cn_opening) {
            if (wall) element.set_wall(wall, transaction_manager);
            element.position = element.wall.compute_abcissa(center) - 0.5 * element.get_width();
            element.update_wall_position();
        }
        else if (element.constructor == cn_object_instance) {
            if (transaction_manager) transaction_manager.push_item_set(element, ["position", "orientation", "_contact_wall", "_contact_wall_side", "_contact_wall_position"]);
            if (this.get_wall()) {
                let actual_wall = wall || element._contact_wall;
                let actual_wall_side = (wall) ? wall_side : element._contact_wall_side
                element.place_on_wall(actual_wall, actual_wall_side, actual_wall.compute_abcissa(center));
            }
            else {
                if (transaction_manager) transaction_manager.push_item_set(element, ["position", "orientation"]);
                element.position = cn_clone(center);
                element.orientation = this.orientation * 180 / Math.PI;
            }
        }
    }

    update_elements(transaction_manager = null, wall = null, wall_side = 0) {
        let previous_wall = this.get_wall();
        if (previous_wall && wall && wall != previous_wall) {
            this.orientation = cn_polar(wall.bounds.direction)[1];
        }

        if (this.elements.length != this.cells[0] * this.cells[1]) {
            let initial_element = this.elements.find(elt => !!elt);
            this.elements = this.elements.filter(elt => elt && this.parent.find_element_by_id(elt.ID));
            while (this.elements.length < this.cells[0] * this.cells[1]) {
                this.clone_element(initial_element, transaction_manager);
            }
            while (this.elements.length > this.cells[0] * this.cells[1]) {
                this.remove_last_element(transaction_manager);
            }
        }

        let dx = cn_cart([this.spacings[0], this.orientation]);
        let dy = cn_cart([this.spacings[1], this.orientation + Math.PI / 2]);
        for (var j = 0; j < this.cells[1]; j++) {
            for (var i = 0; i < this.cells[0]; i++) {
                let center = cn_add(this.center, cn_add(cn_mul(dx, i - this.cells[0] / 2 + 0.5), cn_mul(dy, j - this.cells[1] / 2 + 0.5)));
                this.set_element_position(i + j * this.cells[0], center, wall, wall_side, transaction_manager)
            }
        }

        if (this.elements[0].constructor == cn_opening) {
            if (wall && wall != previous_wall)
                previous_wall.update_openings();
            let new_wall = this.get_wall();
            if (new_wall) new_wall.update_openings();
        }
    }

    _get_field(element) {
        if (element.constructor == cn_column)
            return "columns";
        if (element.constructor == cn_opening)
            return "openings";
        if (element.constructor == cn_object_instance)
            return "object_instances";
        return "";
    }
    clone_element(pattern, transaction_manager) {
        /** @type {cn_element} */
        let new_element = null;
        let field = this._get_field(pattern);
        if (field != "") {
            transaction_manager.push_item_set(pattern.parent, field);
            new_element = pattern.clone();
            pattern.parent[field].push(new_element);
            new_element.duplicator = this;
            this.elements.push(new_element);
        }
        return new_element;
    }

    remove_last_element(transaction_manager) {
        let last_element = this.elements[this.elements.length - 1];
        if (last_element) {
            let field = this._get_field(last_element);
            if (field != "") {
                transaction_manager.push_item_set(last_element.parent, field);
                let index = last_element.parent[field].indexOf(last_element);
                if (index >= 0) last_element.parent[field].splice(index, 1);
            }
        }
        this.elements.splice(this.elements.length - 1, 1);
    }
}

